"use client"

import { useCallback, useMemo, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"

import { FEATURE_FLAGS, useIsFeatureEnabled } from "@/feature-flags"
import { useOptionalWorkspaceContext } from "@/workspaces"
import { cn } from "@coval/ui"
import { History, NotebookText, Plus } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { useCovi } from "../contexts/CoviContext"
import { useCoviAnalytics } from "../hooks/useCoviAnalytics"
import {
  buildSofiaInvestigationHistoryPath,
  buildSofiaNotebooksPath,
  getSofiaHomeThreadId,
  isSofiaHomePath,
  isSofiaHomeSection,
} from "../utils/sofiaHome"
import { CoviThreadList } from "./CoviThreadList"
import { CoviThreadSearchDialog } from "./CoviThreadSearchDialog"

/**
 * What the sidebar widens to while these rows are in it. Chat titles and the
 * "Investigations" row need more than the sidebar's own width, and the
 * module that puts them there owns the number.
 */
export const SOFIA_SIDEBAR_NAV_WIDTH_CLASS = "w-[240px]"

/**
 * How many chats the sidebar lists at rest. Enough to get back to what the user
 * was just in; everything older is one "Show more" away rather than a scroll
 * past the whole history.
 */
const SOFIA_SIDEBAR_RECENT_CHATS = 5

/**
 * Row metrics are SidebarNavItemRow's: a Sofia destination selected here is
 * the same size and shape as a destination selected anywhere else in the
 * sidebar, because it is the same sidebar.
 */
const ROW =
  "group flex w-full items-center gap-2 px-2 py-1 text-left text-sm font-medium text-coval-text transition-colors hover:bg-surface-1 hover:rounded-sm"
const ROW_ACTIVE = "bg-surface-1 rounded-sm"
const ROW_ICON = "h-4 w-4 shrink-0 text-coval-text"

/**
 * Sofia's navigation, unfolded under the sidebar's Sofia entry.
 *
 * Whether these rows are shown at all is the shell's call — the app-shell
 * contribution mounts them under the Sofia entry on every page, and widens the
 * sidebar to hold them. This component only draws them.
 *
 * Home is both Sofia's Daily Briefing and its full-page chat surface, and both
 * want the same destinations and the same thread history beside them. Hanging
 * those under the sidebar entry that leads there — rather than in a second rail
 * next to the sidebar — keeps Home to one navigation column. They stay in the
 * sidebar outside Home too: a recent chat is how the user returns to a
 * conversation from whatever page they wandered off to, so it has to be
 * reachable from there rather than only from the section it lives in.
 *
 * New chat and Daily brief are deliberately NOT rows here. Both are what the
 * Sofia entry above already goes to — its landing is the briefing, and the
 * composer on it starts a fresh chat — so rows for them would be a second and
 * third way to press the same button, spending the top of the section on
 * navigation the user has already done.
 */
export function SofiaSidebarNav() {
  const router = useRouter()
  const queryParams = useQueryParams()
  const workspaceContext = useOptionalWorkspaceContext()
  const globalWorkspaceId = getRouteOrganizationSlug(router.asPath)
    ? undefined
    : (workspaceContext?.currentWorkspace?.id ??
      workspaceContext?.routeWorkspaceId ??
      undefined)
  const {
    threads,
    showThreadOnHome,
    showDailyBriefing,
    renameThread,
    toggleThreadPinned,
    deleteThread,
    orgSlug,
    streamingThreadIds,
    draftThreadIds,
  } = useCovi()
  const { trackConversationLoaded } = useCoviAnalytics()
  const [chatSearchOpen, setChatSearchOpen] = useState(false)
  const activeThreadId = getSofiaHomeThreadId(router.asPath)

  // History is chats the user has actually had. A thread nobody has spoken in
  // is not one to come back to — its row leads to the same blank composer they
  // are already looking at — and the provider always holds one of them, so
  // counting it would mean the section never emptied and never went away. An
  // unsent draft counts: that thread has something in it to lose.
  const startedThreads = useMemo(
    () =>
      threads.filter(
        (thread) => thread.messageCount > 0 || draftThreadIds.has(thread.id),
      ),
    [draftThreadIds, threads],
  )
  const investigationHistoryPath = buildSofiaInvestigationHistoryPath(
    router.asPath,
    orgSlug,
  )
  const notebooksEnabled = useIsFeatureEnabled(FEATURE_FLAGS.SOFIA_NOTEBOOKS)
  const notebooksPath = buildSofiaNotebooksPath(router.asPath, orgSlug)
  const currentPath = (router.asPath ?? "").split("?")[0] ?? ""
  const isNotebooks = currentPath.includes("/home/notebooks")
  // The pages hung below Home are selected by being in the section without
  // being on Home itself — and, for history, without being on a notebook.
  const isInvestigationHistory =
    isSofiaHomeSection(router.asPath) &&
    !isSofiaHomePath(router.asPath) &&
    !isNotebooks

  // Only a real switch counts as a conversation load, matching CoviPanel's own
  // thread list.
  const handleSelectThread = useCallback(
    (threadId: string) => {
      if (threadId !== activeThreadId) {
        trackConversationLoaded()
      }
      showThreadOnHome(threadId)
    },
    [activeThreadId, showThreadOnHome, trackConversationLoaded],
  )

  return (
    // No bottom spacing: in this sidebar a section owns the gap ABOVE it (the
    // shell's own section labels carry `mt-7`, and "Chats" now matches
    // it), so adding space below here would double the gap before whatever
    // section comes next.
    <nav aria-label="Sofia chat history" className="flex flex-col gap-px">
      <Link
        href={investigationHistoryPath}
        aria-current={isInvestigationHistory ? "page" : undefined}
        className={cn(ROW, isInvestigationHistory && ROW_ACTIVE)}
      >
        <History className={ROW_ICON} aria-hidden />
        <span className="min-w-0 truncate">Investigations</span>
      </Link>
      {notebooksEnabled ? (
        <div className={cn(ROW, "pr-1", isNotebooks && ROW_ACTIVE)}>
          <Link
            href={notebooksPath}
            aria-current={isNotebooks ? "page" : undefined}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <NotebookText className={ROW_ICON} aria-hidden />
            <span className="min-w-0 truncate">Notebooks</span>
          </Link>
          <Link
            href={queryParams.getHref(
              buildSofiaNotebooksPath(router.asPath, orgSlug, "scheduled"),
              orgSlug ?? undefined,
              globalWorkspaceId,
            )}
            aria-label="New notebook"
            data-testid="sidebar-notebooks-create-link"
            className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-coval-text-muted opacity-0 transition-opacity hover:bg-surface-2 hover:text-coval-text group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
      ) : null}
      {/* The list draws its own "Chats" label with search folded into it, and
          empties itself while that search is open until something is typed. Capped and scrolling in place: a long history pushed into the
          sidebar's own scroll area would put every section below Sofia — and
          the org switcher under them — behind a scroll past every chat.

          With no chats the whole section goes, label and all. A heading over an
          empty list is a section the nav is carrying for nothing, and the panel
          variants' "No chats yet" line does not belong here: there the list is
          the surface and has to say why it is blank, whereas here it is one
          section of a sidebar that can simply not have it yet. */}
      {startedThreads.length > 0 ? (
        <div className="flex max-h-[45vh] min-h-0 flex-col">
          <CoviThreadList
            threads={startedThreads}
            activeThreadId={activeThreadId}
            streamingThreadIds={streamingThreadIds}
            onSelect={handleSelectThread}
            onRename={renameThread}
            onTogglePin={toggleThreadPinned}
            onDelete={deleteThread}
            // Sofia's page rather than a thread of its own: the landing is the
            // blank composer a chat starts from, with the day's investigations
            // under it, and a chat only exists once something is said in it.
            onNewChat={showDailyBriefing}
            onSearch={() => setChatSearchOpen(true)}
            showHeader={false}
            variant="sidebar"
            collapseAfter={SOFIA_SIDEBAR_RECENT_CHATS}
          />
        </div>
      ) : null}
      {/* The search itself is a palette over the page, not a field in this
          column: 240px is not a surface to read a search of the whole history
          in, and the nav stays exactly as it is behind it. */}
      <CoviThreadSearchDialog
        open={chatSearchOpen}
        onOpenChange={setChatSearchOpen}
        threads={startedThreads}
        onSelect={handleSelectThread}
      />
    </nav>
  )
}
